import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api.js";
import type { ReportSummary } from "./types.js";
import { useT } from "../../i18n/index.js";

const s = {
  page: {
    maxWidth: "900px",
    margin: "0 auto",
    padding: "var(--space-lg) var(--space-md) var(--space-xl)",
    color: "var(--text)",
  },
  heading: { margin: "0 0 1.5rem", fontSize: "var(--text-xl)", fontWeight: 700 },
  tableWrap: {
    overflowX: "auto" as const,
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border)",
  },
  table: { width: "100%", borderCollapse: "collapse" as const },
  th: {
    textAlign: "left" as const,
    padding: "0.75rem 1rem",
    background: "var(--bg-deep)",
    color: "var(--text-mid)",
    fontSize: "var(--text-xs)",
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    borderBottom: "2px solid var(--border)",
  },
  td: {
    padding: "0.75rem 1rem",
    borderBottom: "1px solid var(--border)",
    verticalAlign: "middle" as const,
    fontSize: "var(--text-sm)",
    color: "var(--text)",
  },
  badge: (phase: string) => ({
    display: "inline-block",
    padding: "0.2rem 0.6rem",
    borderRadius: "999px",
    fontSize: "var(--text-xs)",
    fontWeight: 600,
    background: phase === "ended" ? "var(--success-lt)" : "var(--error-lt)",
    color: phase === "ended" ? "var(--success)" : "var(--error)",
  }),
  empty: { color: "var(--text-dim)", fontStyle: "italic" as const, padding: "2rem 0" },
  error: { color: "var(--error)" },
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

export function ReportsList() {
  const t = useT();
  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ reports: ReportSummary[] }>("/reports")
      .then((res) => setReports(res.reports))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : t("reports.loading")))
      .finally(() => setLoading(false));
  }, [t]);

  if (loading) return <div style={s.page}><p>{t("reports.loading")}</p></div>;
  if (error) return <div style={s.page}><p style={s.error}>{error}</p></div>;

  return (
    <div style={s.page}>
      <h1 style={s.heading}>{t("reports.title")}</h1>
      {reports.length === 0 ? (
        <p style={s.empty}>{t("reports.empty")}</p>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table} aria-label={t("reports.sessionReports")}>
            <thead>
              <tr>
                <th style={s.th}>{t("reports.quiz")}</th>
                <th style={s.th}>{t("reports.ended")}</th>
                <th style={s.th}>{t("reports.players")}</th>
                <th style={s.th}>{t("reports.questions")}</th>
                <th style={s.th}>{t("reports.status")}</th>
                <th style={s.th}>{t("reports.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.sessionId}>
                  <td style={s.td}>{r.quizTitle}</td>
                  <td style={s.td}>{formatDate(r.endedAt)}</td>
                  <td style={s.td}>{r.playerCount}</td>
                  <td style={s.td}>{r.questionCount}</td>
                  <td style={s.td}><span style={s.badge(r.phase)}>{r.phase}</span></td>
                  <td style={s.td}>
                    <Link to={`/admin/reports/${r.sessionId}`} style={{ fontWeight: 600 }}>
                      {t("reports.view")}
                    </Link>
                    {" · "}
                    <a
                      href={`/api/reports/${r.sessionId}/csv`}
                      style={{ fontWeight: 600 }}
                      download
                      aria-label={t("reports.downloadCsvFor", { quiz: r.quizTitle })}
                    >
                      CSV
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
